import { sendClientPasswordReset } from './clientAuthService';

export interface EmailCheckResult {
  exists: boolean;
  error?: string;
}

export interface PasswordRecoveryResult {
  success: boolean;
  message?: string;
  error?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmailFormat(email: string): boolean {
  return EMAIL_REGEX.test(email.trim().toLowerCase());
}

/**
 * Consulta la API del servidor para validar si el correo ya existe registrado en Firestore o Firebase Auth.
 */
export async function checkEmailExistsApi(email: string): Promise<EmailCheckResult> {
  const cleanEmail = email.trim().toLowerCase();
  if (!isValidEmailFormat(cleanEmail)) {
    return { exists: false, error: 'Formato de correo electrónico inválido.' };
  }

  try {
    const res = await fetch('/api/auth/check-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      return { exists: false, error: data.error || 'Error al validar correo en el servidor.' };
    }
    return { exists: Boolean(data.exists) };
  } catch (err: any) {
    return { exists: false, error: err?.message || 'Error de conexión al validar el correo.' };
  }
}

/**
 * Ejecuta el flujo completo de recuperación con validación previa de existencia del correo.
 */
export async function requestPasswordRecovery(email: string): Promise<PasswordRecoveryResult> {
  const cleanEmail = email.trim().toLowerCase();

  if (!isValidEmailFormat(cleanEmail)) {
    return {
      success: false,
      error: 'Por favor ingresa un formato de correo electrónico válido.',
    };
  }

  // 1. Validar primero si el correo existe
  const check = await checkEmailExistsApi(cleanEmail);
  if (check.error) {
    return { success: false, error: check.error };
  }

  if (!check.exists) {
    return {
      success: false,
      error: 'El correo electrónico no se encuentra registrado en el sistema. Verifica la dirección o regístrate.',
    };
  }

  // 2. Si el correo existe, proceder con el envío del enlace de recuperación
  try {
    await sendClientPasswordReset(cleanEmail);
    return {
      success: true,
      message: `Se ha enviado un correo a ${cleanEmail} para restablecer tu contraseña. Revisa tu bandeja de entrada o spam.`,
    };
  } catch (err: any) {
    // Fallback a endpoint de servidor si falla cliente directo
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail }),
      });
      const data = await res.json();
      if (data.success) {
        return {
          success: true,
          message: `Se ha enviado el enlace de restablecimiento para ${cleanEmail}. Revisa tu bandeja de entrada o spam.`,
        };
      }
      return {
        success: false,
        error: data.error || 'No se pudo enviar el correo de recuperación.',
      };
    } catch {
      return {
        success: false,
        error: err?.message || 'Error al enviar enlace de restablecimiento.',
      };
    }
  }
}
